import { c as _c } from "react-compiler-runtime";
import { useCallback } from 'react';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { Metrics } from 'framework-listing-lib/internal/metrics';
import useApplicationId from 'framework-listing-lib/internal/hooks/useApplicationId';
import { useCurrentViewId, useViewsNamespace } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import { convertViewToCustomerDataView } from '../utils/mapper';
import { useUpdateSingleDefaultViewMutation } from 'customer-data-views-management/singleView/hooks/useUpdateSingleDefaultViewMutation';
import { useFetchSingleView } from 'customer-data-views-management/singleView/hooks/useFetchSingleView';

// This should be correct enough to prevent render-time errors
// while the currentView loads
// but should also not overwrite any actual existing view
// if somehow the mutation is called during the loading state
export const PLACEHOLDER_VIEW = {
  id: '',
  name: '',
  columns: [],
  state: {},
  filterGroups: [],
  quickFilters: [],
  quickFilterProperties: [],
  namespaceName: '',
  objectTypeId: '',
  ownerId: undefined,
  private: false,
  teamId: undefined,
  type: 'STANDARD'
};

/**
 * Saving a default view is a silent, auto-save style operation: it reports
 * success/failure through metrics only, never through a localized alert.
 *
 * Keep this module free of I18n imports (directly or transitively). It is
 * reachable from `ViewsProvider`, which every consumer of the library mounts,
 * so pulling translations in here forces every downstream app to bundle
 * `framework-listing-lib/lang/*.lyaml` (enforced by the
 * `validate-dependency-lyaml-present` build validation).
 */
export function useSaveDefaultView() {
  "use memo";

  const $ = _c(12);
  const objectType = useObjectTypeId();
  const namespace = useViewsNamespace();
  const currentViewId = useCurrentViewId();
  const applicationId = useApplicationId();
  const t0 = currentViewId || "";
  let t1;
  if ($[0] !== namespace || $[1] !== objectType || $[2] !== t0) {
    t1 = {
      id: t0,
      objectTypeId: objectType,
      namespaceName: namespace
    };
    $[0] = namespace;
    $[1] = objectType;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data: currentViewData
  } = useFetchSingleView(t1);
  const t2 = (currentViewData === null || currentViewData === void 0 ? void 0 : currentViewData.singleViewById) || PLACEHOLDER_VIEW;
  let t3;
  if ($[4] !== t2) {
    t3 = {
      view: t2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [updateDefaultViewMutation, t4] = useUpdateSingleDefaultViewMutation(t3);
  const {
    loading
  } = t4;
  let t5;
  if ($[6] !== applicationId || $[7] !== updateDefaultViewMutation) {
    t5 = view => updateDefaultViewMutation({
      variables: convertViewToCustomerDataView({
        view
      })
    }).then(() => {
      Metrics.counter("update-default-view-success", {
        application_id: applicationId
      }).increment();
    }).catch(() => {
      Metrics.counter("update-default-view-failure", {
        application_id: applicationId
      }).increment();
    });
    $[6] = applicationId;
    $[7] = updateDefaultViewMutation;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const saveDefaultView = t5;
  let t6;
  if ($[9] !== loading || $[10] !== saveDefaultView) {
    t6 = {
      loading,
      saveDefaultView
    };
    $[9] = loading;
    $[10] = saveDefaultView;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}